import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { styled, type SxProps, type Theme } from '@mui/material/styles';

export const SectionWrapper = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1.5),
}));

export const SectionAccordion = styled(Accordion)(() => ({
  backgroundColor: 'transparent',
  width: '100%',
  boxShadow: 'none',
  '&::before': {
    display: 'none',
  },
  '&.MuiAccordion-root': {
    margin: 0,
  },
}));

export const SectionAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  minHeight: 'auto',
  gap: theme.spacing(1.5),
  flexDirection: 'row-reverse',
  justifyContent: 'flex-end',
  borderBottom: `${theme.typography.pxToRem(1)} solid ${theme.palette.divider}`,
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.MuiAccordionSummary-root.Mui-expanded': {
    minHeight: 'auto',
    borderBottomColor: 'transparent',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: theme.palette.text.secondary,
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '&.MuiAccordionSummary-root': {
    borderBottom: 'none',
    '&:hover': { backgroundColor: 'transparent' },
  },
}));

export const SectionAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: 0,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

export const SectionSummaryContent = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
}));

export const SectionSummaryTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: theme.typography.pxToRem(14),
  color: theme.palette.text.primary,
}));

export const PlansCounterChip = styled(Chip)(({ theme }) => ({
  height: theme.spacing(2.5),
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 500,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.action.selected,
  borderRadius: theme.spacing(1.25),
  marginLeft: theme.spacing(1),
}));

export const BillingOptionsList = styled(Stack)(({ theme }) => ({
  '& .MuiFormControl-root': {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    margin: 0,
    borderBottom: `${theme.typography.pxToRem(1)} solid ${theme.palette.divider}`,
  },
}));

export const billingOptionFormControlSx: SxProps<Theme> = {
  width: '100%',
  m: 0,
};

export const billingOptionTitleSx: SxProps<Theme> = {
  fontWeight: 500,
  fontSize: (theme: Theme) => theme.typography.pxToRem(14),
  color: (theme: Theme) => theme.palette.text.primary,
};

export const PlansList = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
}));

export const PlanAccordionWrapper = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&::before': {
    display: 'none',
  },
  '&:not(:last-of-type)': {
    borderBottom: `${theme.typography.pxToRem(1)} solid ${theme.palette.divider}`,
  },
  '&.Mui-expanded:not(:last-of-type)': {
    borderBottomColor: 'transparent',
  },
}));

export const PlanAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(1.5, 2.5),
  minHeight: 'auto',
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  flexDirection: 'row-reverse',
  justifyContent: 'flex-end',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&[data-disabled="true"]': {
    cursor: 'default',
  },
  '&[data-disabled="true"]:hover': {
    backgroundColor: 'transparent',
  },
  '&.MuiAccordionSummary-root': {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: theme.palette.text.secondary,
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '&.MuiAccordionSummary-root.Mui-expanded': {
    minHeight: 'auto',
  },
}));

export const PlanAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(1.5, 1.5, 2),
  borderTop: `0.0625rem solid ${theme.palette.divider}`,
}));

export const PlanName = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  color: theme.palette.text.primary,
  flexGrow: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const PlanInactiveText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  color: theme.palette.text.secondary,
}));

export const PlanToggleItems = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
}));

export const planControlFormSx: SxProps<Theme> = {
  width: '100%',
  m: 0,
};

export const planToggleLabelSx: SxProps<Theme> = {
  width: '100%',
  margin: 0,
  flexDirection: 'row-reverse',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: (theme: Theme) => theme.spacing(2),
  padding: (theme: Theme) => `${theme.spacing(1.5)} 0`,
  '& .MuiFormControlLabel-label': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flexGrow: 1,
    minWidth: 0,
  },
};
export const planToggleSx: SxProps<Theme> = {
  fontWeight: 500,
  fontSize: (theme: Theme) => theme.typography.pxToRem(14),
  color: (theme: Theme) => theme.palette.text.primary,
};

export const planHeaderToggleFormSx: SxProps<Theme> = {
  width: 'auto',
  m: 0,
  ml: 'auto',
};

export const planHeaderToggleLabelSx: SxProps<Theme> = {
  m: 0,
  gap: 0,
  flexDirection: 'row-reverse',
  alignItems: 'center',
};
